import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { X, Plus, ImagePlus, GripVertical, Link as LinkIcon, LayoutList } from "lucide-react";
import api, { formatApiError, fileUrl } from "../lib/api";
import { toast } from "sonner";

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "blockquote"],
    ["clean"],
  ],
};

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.path;
}

export default function EditTopicModal({ open, topic, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [tips, setTips] = useState([""]);
  const [imagePath, setImagePath] = useState(null);
  const [sections, setSections] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [links, setLinks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (topic) {
      setTitle(topic.title || "");
      setSubtitle(topic.subtitle || "");
      setDescription(topic.description || "");
      setTips(topic.tips && topic.tips.length ? topic.tips : [""]);
      setImagePath(topic.image_path || null);
      setSections(topic.sections || []);
      setGallery(topic.gallery || []);
      setLinks(topic.links || []);
    }
  }, [topic]);

  if (!topic) return null;

  const onHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadFile(file);
      setImagePath(path);
      toast.success("Imagem principal enviada");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const onGalleryFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        paths.push(await uploadFile(f));
      }
      setGallery((prev) => [...prev, ...paths]);
      toast.success(`${paths.length} imagem(ns) enviada(s)`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const onSectionImage = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const path = await uploadFile(file);
      setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, image_path: path } : s)));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title, subtitle, description,
        tips: tips.filter((t) => t.trim()),
        image_path: imagePath == null ? "" : imagePath,
        sections: sections.map((s) => ({ heading: s.heading || "", body: s.body || "", image_path: s.image_path || null })),
        gallery,
        links: links.filter((l) => l.label?.trim() && l.url?.trim()),
      };
      const { data } = await api.put(`/moderation/topics/${topic.topic_id}`, payload);
      toast.success("Tópico atualizado");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="edit-topic-modal" className="max-w-4xl max-h-[92vh] overflow-y-auto bg-white rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Editar tópico: {topic.title}</DialogTitle>
          <DialogDescription className="text-brand-textMuted">
            Edite todas as informações do guia — textos, seções, dicas, galeria e links úteis.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-8 mt-2">
          {/* BÁSICO */}
          <fieldset className="space-y-4">
            <legend className="font-heading font-bold text-lg text-brand-text">Cabeçalho</legend>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Título</label>
              <Input data-testid="topic-title" required value={title} onChange={(e) => setTitle(e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-1">Subtítulo</label>
              <Input data-testid="topic-subtitle" value={subtitle} onChange={(e) => setSubtitle(e.target.value)} className="bg-brand-bg border-brand-border rounded-2xl h-11" />
            </div>
            <div>
              <label className="block text-sm font-bold text-brand-text mb-2">Imagem principal (hero)</label>
              <div className="flex items-center gap-4">
                {imagePath && <img src={fileUrl(imagePath)} alt="hero" className="w-24 h-24 object-cover rounded-2xl border border-brand-border" />}
                <label className="flex-1 flex items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-brand-border bg-brand-bg p-4 cursor-pointer hover:border-brand-primary transition-colors">
                  <ImagePlus className="w-5 h-5 text-brand-primary" />
                  <span className="font-bold text-brand-text text-sm">{imagePath ? "Trocar imagem" : "Escolher imagem"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={onHeroFile} data-testid="topic-image-input" />
                </label>
                {imagePath && (
                  <Button type="button" variant="outline" onClick={() => setImagePath(null)} className="rounded-full font-bold" data-testid="topic-image-clear">Remover</Button>
                )}
              </div>
            </div>
          </fieldset>

          {/* RESUMO */}
          <fieldset className="space-y-2">
            <legend className="font-heading font-bold text-lg text-brand-text">Resumo (O que é)</legend>
            <p className="text-xs text-brand-textMuted">Aparece no topo da página do guia. Use formatação rica se quiser.</p>
            <div className="rounded-2xl bg-white border border-brand-border overflow-hidden">
              <ReactQuill theme="snow" value={description} onChange={setDescription} modules={QUILL_MODULES} placeholder="Explique o que é essa restrição..." />
            </div>
          </fieldset>

          {/* SEÇÕES */}
          <fieldset className="space-y-4">
            <div className="flex items-center justify-between">
              <legend className="font-heading font-bold text-lg text-brand-text inline-flex items-center gap-2"><LayoutList className="w-5 h-5" /> Seções livres</legend>
              <Button type="button" variant="outline" size="sm" data-testid="add-section-btn" onClick={() => setSections((p) => [...p, { heading: "", body: "", image_path: null }])} className="rounded-full font-bold">
                <Plus className="w-4 h-4 mr-1" /> Adicionar seção
              </Button>
            </div>
            <p className="text-xs text-brand-textMuted">Crie blocos livres com título e conteúdo (ex: Sintomas, Diagnóstico, Alimentos permitidos...).</p>
            {sections.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-brand-border p-8 text-center text-brand-textMuted text-sm">Nenhuma seção adicionada.</div>
            ) : (
              <div className="space-y-4">
                {sections.map((s, i) => (
                  <div key={`section-${i}`} data-testid={`section-${i}`} className="rounded-2xl border border-brand-border bg-brand-bg p-4 space-y-3">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-brand-textMuted" />
                      <Input
                        data-testid={`section-heading-${i}`}
                        placeholder="Título da seção (ex: Sintomas)"
                        value={s.heading}
                        onChange={(e) => setSections((p) => p.map((x, idx) => idx === i ? { ...x, heading: e.target.value } : x))}
                        className="bg-white border-brand-border rounded-xl h-10 font-bold"
                      />
                      <div className="flex gap-1">
                        {i > 0 && <Button type="button" variant="outline" size="icon" data-testid={`section-up-${i}`} onClick={() => setSections((p) => { const c = [...p]; [c[i - 1], c[i]] = [c[i], c[i - 1]]; return c; })} className="rounded-full shrink-0">↑</Button>}
                        {i < sections.length - 1 && <Button type="button" variant="outline" size="icon" data-testid={`section-down-${i}`} onClick={() => setSections((p) => { const c = [...p]; [c[i + 1], c[i]] = [c[i], c[i + 1]]; return c; })} className="rounded-full shrink-0">↓</Button>}
                        <Button type="button" variant="outline" size="icon" data-testid={`section-remove-${i}`} onClick={() => setSections((p) => p.filter((_, idx) => idx !== i))} className="rounded-full border-red-200 text-red-700 hover:bg-red-50 shrink-0">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="rounded-xl bg-white border border-brand-border overflow-hidden">
                      <ReactQuill theme="snow" value={s.body} onChange={(v) => setSections((p) => p.map((x, idx) => idx === i ? { ...x, body: v } : x))} modules={QUILL_MODULES} placeholder="Conteúdo da seção..." />
                    </div>
                    <div className="flex items-center gap-3">
                      {s.image_path && <img src={fileUrl(s.image_path)} alt="section" className="w-16 h-16 object-cover rounded-xl border border-brand-border" />}
                      <label className="flex-1 flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-brand-border bg-white p-3 cursor-pointer hover:border-brand-primary text-sm">
                        <ImagePlus className="w-4 h-4 text-brand-primary" />
                        <span className="font-bold text-brand-text">{s.image_path ? "Trocar imagem" : "Adicionar imagem à seção (opcional)"}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => onSectionImage(i, e)} data-testid={`section-image-${i}`} />
                      </label>
                      {s.image_path && (
                        <Button type="button" variant="outline" size="sm" data-testid={`section-image-clear-${i}`} onClick={() => setSections((p) => p.map((x, idx) => idx === i ? { ...x, image_path: null } : x))} className="rounded-full font-bold">Remover</Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </fieldset>

          {/* DICAS */}
          <fieldset className="space-y-3">
            <div className="flex items-center justify-between">
              <legend className="font-heading font-bold text-lg text-brand-text">Dicas de cuidado</legend>
              <Button type="button" variant="outline" size="sm" onClick={() => setTips((p) => [...p, ""])} className="rounded-full font-bold" data-testid="add-tip-btn">
                <Plus className="w-4 h-4 mr-1" /> Adicionar dica
              </Button>
            </div>
            <div className="space-y-2">
              {tips.map((t, i) => (
                <div key={`tip-input-${i}`} className="flex items-center gap-2">
                  <Textarea
                    data-testid={`tip-input-${i}`}
                    rows={2}
                    value={t}
                    onChange={(e) => setTips((prev) => prev.map((v, idx) => idx === i ? e.target.value : v))}
                    placeholder={`Dica ${i + 1}`}
                    className="bg-brand-bg border-brand-border rounded-2xl flex-1"
                  />
                  {tips.length > 1 && (
                    <Button type="button" variant="outline" size="icon" onClick={() => setTips((prev) => prev.filter((_, idx) => idx !== i))} className="rounded-full border-red-200 text-red-700 hover:bg-red-50 shrink-0" data-testid={`remove-tip-${i}`}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </fieldset>

          {/* GALERIA */}
          <fieldset className="space-y-3">
            <div className="flex items-center justify-between">
              <legend className="font-heading font-bold text-lg text-brand-text">Galeria de imagens</legend>
              <label className="rounded-full bg-brand-primary text-white font-bold px-4 py-1.5 text-sm cursor-pointer hover:bg-brand-primaryHover inline-flex items-center gap-1">
                <Plus className="w-4 h-4" /> Adicionar imagens
                <input type="file" accept="image/*" multiple className="hidden" onChange={onGalleryFile} data-testid="gallery-input" />
              </label>
            </div>
            {gallery.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-brand-border p-8 text-center text-brand-textMuted text-sm">Nenhuma imagem na galeria.</div>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {gallery.map((p, i) => (
                  <div key={p} data-testid={`gallery-item-${i}`} className="relative group">
                    <img src={fileUrl(p)} alt={`gallery ${i}`} className="w-full aspect-square object-cover rounded-2xl border border-brand-border" />
                    <button type="button" data-testid={`gallery-remove-${i}`} onClick={() => setGallery((prev) => prev.filter((_, idx) => idx !== i))} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white shadow-md text-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </fieldset>

          {/* LINKS */}
          <fieldset className="space-y-3">
            <div className="flex items-center justify-between">
              <legend className="font-heading font-bold text-lg text-brand-text inline-flex items-center gap-2"><LinkIcon className="w-5 h-5" /> Links úteis</legend>
              <Button type="button" variant="outline" size="sm" data-testid="add-link-btn" onClick={() => setLinks((p) => [...p, { label: "", url: "" }])} className="rounded-full font-bold">
                <Plus className="w-4 h-4 mr-1" /> Adicionar link
              </Button>
            </div>
            {links.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-brand-border p-8 text-center text-brand-textMuted text-sm">Nenhum link adicionado.</div>
            ) : (
              <div className="space-y-2">
                {links.map((l, i) => (
                  <div key={`link-${i}`} className="flex items-center gap-2">
                    <Input data-testid={`link-label-${i}`} placeholder="Nome (ex: FENACELBRA)" value={l.label} onChange={(e) => setLinks((p) => p.map((x, idx) => idx === i ? { ...x, label: e.target.value } : x))} className="bg-brand-bg border-brand-border rounded-2xl h-11 flex-1" />
                    <Input data-testid={`link-url-${i}`} placeholder="https://..." value={l.url} onChange={(e) => setLinks((p) => p.map((x, idx) => idx === i ? { ...x, url: e.target.value } : x))} className="bg-brand-bg border-brand-border rounded-2xl h-11 flex-1" />
                    <Button type="button" variant="outline" size="icon" data-testid={`link-remove-${i}`} onClick={() => setLinks((p) => p.filter((_, idx) => idx !== i))} className="rounded-full border-red-200 text-red-700 hover:bg-red-50 shrink-0">
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </fieldset>

          <DialogFooter className="gap-2 sticky bottom-0 bg-white pt-4 border-t border-brand-border">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full font-bold">Cancelar</Button>
            <Button type="submit" data-testid="topic-save-btn" disabled={saving || uploading} className="bg-brand-primary hover:bg-brand-primaryHover text-white rounded-full font-bold">
              {saving ? "Salvando..." : uploading ? "Enviando imagens..." : "Salvar tudo"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
